import React, {useEffect, useRef, useState} from "react";
import VehicleCondition from "../../utils/VehicleCondition";
import VehicleEquipment from "./VehicleEquipment";
import ContactDialog from "../../components/ContactDialog/ContactDialog";
import calendarIcon  from "../../assets/images/icons/car/calendar.svg";
import gearboxIcon   from "../../assets/images/icons/car/gearbox.svg";
import petrolIcon    from "../../assets/images/icons/car/petrol.svg";
import engineIcon    from "../../assets/images/icons/car/engine.svg";
import carSvcIcon    from "../../assets/images/icons/car/car-service.svg";
import akolBanner    from "../../assets/images/content/wiarygodny_dostawca.png";
import heightIcon    from "../../assets/images/icons/car/dims/icon-height.svg";
import widthIcon     from "../../assets/images/icons/car/dims/icon-width.svg";
import lengthIcon    from "../../assets/images/icons/car/dims/icon-length.svg";
import trunkIcon     from "../../assets/images/icons/car/dims/icon-trunk.svg";

function VehicleInfo({offer}) {

    const [activeTab, setActiveTab] = useState('dane');
    const [isOpenContactDialog, setIsOpenContactDialog] = useState(false);
    const [isTabBarScrollable, setIsTabBarScrollable] = useState(false);
    const tabBarRef = useRef(null);

    const salesDocumentMapper = () => {
        switch (offer.salesDocument) {
            case 'FAKTURA_VAT':  return 'Faktura VAT';
            case 'VAT_MARZA':   return 'Faktura VAT Marża';
            case 'UMOWA_KS':    return 'Umowa kupna-sprzedaży';
            case 'VAT_ZW':      return 'Faktura VAT zwolniony';
            default:            return offer.salesDocument;
        }
    };

    const isWiarygodny = () => {
        const s = offer.id.toString();
        return s.length === 10 && (s.startsWith('2') || s.startsWith('3'));
    };

    /* ── icon stats ── */
    const stats = [
        {icon: calendarIcon, label: 'Rok produkcji',      value: offer.manufactureYear},
        {icon: gearboxIcon,  label: 'Skrzynia biegów',    value: offer.gearboxType},
        {icon: petrolIcon,   label: 'Rodzaj paliwa',      value: offer.fuelType},
        {icon: carSvcIcon,   label: 'Przebieg',           value: `${offer.mileage} km`},
        {icon: engineIcon,   label: 'Pojemność skokowa',  value: `${offer.engineCapacity} cm³`},
    ];

    /* ── data-table rows, 3 columns ── */
    const col1 = [
        {label: 'Stan',               value: VehicleCondition[offer.condition]},
        {label: 'Dokument sprzedaży', value: salesDocumentMapper()},
        {label: 'Liczba siedzeń',     value: offer.numberOfSeats},
    ];
    const col2 = [
        {label: 'Napęd', value: offer.driveType},
        {label: 'Kolor', value: offer.color},
        {
            label: 'VIN',
            value: offer.vin
                ? offer.vin
                : (
                    <button
                        type="button"
                        className="vehicle-tabs__ask-btn"
                        onClick={() => setIsOpenContactDialog(true)}
                    >
                        Zapytaj doradcę
                    </button>
                )
        },
    ];
    const col3 = [
        {label: 'Moc silnika',   value: `${offer.enginePowerHP} KM`},
        offer.grossVehicleWeight
            ? {label: 'DMC',   value: `${offer.grossVehicleWeight} kg`}
            : null,
        {label: 'Rodzaj nadwozia', value: offer.bodyType},
        offer.numberOfDoors ? {label: 'Liczba drzwi', value: offer.numberOfDoors} : null,
        offer.range && offer.fuelType === 'Elektryczny'
            ? {label: 'Zasięg', value: `${offer.range} km`}
            : null,
    ].filter(Boolean);

    const renderTableCol = (rows) => rows.map((row, i) => (
        <div key={i} className="vehicle-tabs__row">
            <span className="vehicle-tabs__row-label">{row.label}</span>
            <span className="vehicle-tabs__row-value">{row.value}</span>
        </div>
    ));

    /* ── dimensions ── */
    const dims = [
        offer.height       ? {icon: heightIcon, label: 'Wysokość',           value: `${offer.height} cm`}     : null,
        offer.width        ? {icon: widthIcon,  label: 'Szerokość',           value: `${offer.width} cm`}      : null,
        offer.length       ? {icon: lengthIcon, label: 'Długość',             value: `${offer.length} cm`}     : null,
        offer.trunkVolume  ? {icon: trunkIcon,  label: 'Pojemność bagażnika', value: `${offer.trunkVolume} l`} : null,
    ].filter(Boolean);

    /* ── tabs — hide WYMIARY / OPIS when there's nothing to show ── */
    const TABS = [
        {id: 'dane',       label: 'DANE POJAZDU'},
        {id: 'wyposazenie', label: 'WYPOSAŻENIE'},
        dims.length > 0 && offer.condition === 'NEW'   ? {id: 'wymiary', label: 'WYMIARY'} : null,
        offer.description  ? {id: 'opis',    label: 'OPIS'}    : null,
    ].filter(Boolean);

    const currentTab = TABS.find(t => t.id === activeTab) ? activeTab : TABS[0].id;

    useEffect(() => {
        const el = tabBarRef.current;
        if (!el) return;
        const checkOverflow = () => setIsTabBarScrollable(el.scrollWidth > el.clientWidth + 1);
        checkOverflow();
        window.addEventListener('resize', checkOverflow);
        return () => window.removeEventListener('resize', checkOverflow);
    }, [TABS.length]);

    /* ── tab content map ── */
    const content = {
        dane: (
            <div className="vehicle-tabs__dane">
                {isWiarygodny() && (
                    <img src={akolBanner} alt="Wiarygodny dostawca" className="vehicle-tabs__akol"/>
                )}
                <div className="vehicle-tabs__stats">
                    {stats.map((s, i) => (
                        <div key={i} className="vehicle-tabs__stat">
                            <img src={s.icon} alt="" className="vehicle-tabs__stat-icon"/>
                            <div className="vehicle-tabs__stat-txt">
                                <span className="vehicle-tabs__stat-label">{s.label}</span>
                                <span className="vehicle-tabs__stat-value">{s.value}</span>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="vehicle-tabs__table">
                    <div className="vehicle-tabs__col">{renderTableCol(col1)}</div>
                    <div className="vehicle-tabs__col">{renderTableCol(col2)}</div>
                    <div className="vehicle-tabs__col">{renderTableCol(col3)}</div>
                </div>
            </div>
        ),

        wyposazenie: <VehicleEquipment offer={offer}/>,

        wymiary: (
            <div className="vehicle-tabs__wymiary">
                <div className="vehicle-tabs__dims">
                    {dims.map((d, i) => (
                        <div key={i} className="vehicle-tabs__dim">
                            <img src={d.icon} alt="" className="vehicle-tabs__dim-icon"/>
                            <div className="vehicle-tabs__dim-txt">
                                <span className="vehicle-tabs__stat-label">{d.label}</span>
                                <span className="vehicle-tabs__stat-value">{d.value}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        ),

        opis: (
            <div className="vehicle-tabs__opis" dangerouslySetInnerHTML={{__html: offer.description}}/>
        ),
    };

    return (
        <div className="vehicle-tabs">
            <div className={'vehicle-tabs__bar-wrap' + (isTabBarScrollable ? ' vehicle-tabs__bar-wrap--scrollable' : '')}>
                <div className="vehicle-tabs__bar" ref={tabBarRef}>
                    {TABS.map((tab) => (
                        <button
                            key={tab.id}
                            type="button"
                            className={'vehicle-tabs__tab' + (currentTab === tab.id ? ' vehicle-tabs__tab--active' : '')}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
                <div className="vehicle-tabs__bar-fade" aria-hidden="true"/>
            </div>
            <div className="vehicle-tabs__content">
                {content[currentTab]}
            </div>
            <ContactDialog
                isOpen={isOpenContactDialog}
                onClose={() => setIsOpenContactDialog(false)}
                additionalMessage={"Pytanie o numer VIN dla oferty: " + window.location.href}
            />
        </div>
    );
}

export default VehicleInfo;
